import {DocumentDuplicateIcon, ExternalLinkIcon} from '@heroicons/react/solid';

import {Badge, Button, Link} from '@/components';
import type {AfriexBusinessUser} from '@/types/AdminBusinessUser';
import type {AfriexBusiness} from '@/types/AfriexBusiness';
import {statusColors} from '@/types/Transaction';
import {copyToClipboard} from '@/utils/user';

type MerchantListSummary = {
  business: AfriexBusiness;
  person: AfriexBusinessUser;
  iskyckybScreen?: boolean;
};

const MerchantListSummary = ({business, person}: MerchantListSummary) => {
  const {isApprovedForAPI = false} = business?.meta?.flags ?? {};

  return (
    <div
      data-cy="merchant-business-row-summary"
      className="bg-white dark:bg-transparent rounded-lg w-full px-5 sm:px-5 py-5 sm:py-5"
    >
      <div className="flex items-center gap-4 mb-3">
        <h2 className="font-semibold text-2xl">{business.name}</h2>
      </div>
      <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900">
        <p className="flex-[0.4] text-slate-500 text-sm  dark:text-slate-100">
          Business ID
        </p>
        <div className="flex-[0.6] flex flex-row text-sm font-medium text-gray-700 gap-2 w-1/2 dark:text-gray-100 ">
          <div className="flex-1 flex items-center gap-2 truncate">
            <span className="truncate">{business.id}</span>
            <span
              title="click to copy userID"
              onClick={() => copyToClipboard(business?.id || '')}
            >
              <DocumentDuplicateIcon className="w-4 h-4 text-cyan-700 cursor-pointer" />
            </span>
          </div>
        </div>
      </div>
      <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900">
        <p className="flex-[0.4] text-slate-500 text-sm  dark:text-slate-100">
          Business Profile
        </p>
        <p className="flex-[0.6] text-sm font-medium text-gray-700  w-1/2 dark:text-gray-100 ">
          <Link
            to={`/merchants/businesses/${business.id}`}
            target="_blank"
            data-cy="merchant-business-profile-link"
            rel="noopener noreferrer"
          >
            <Button
              variant="ghost"
              className="no-padding no-margin hover:underline"
              rightIcon={<ExternalLinkIcon className="w-4 h-4" />}
            >
              View Business Profile
            </Button>
          </Link>
        </p>
      </div>
      <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900">
        <p className="flex-[0.4] text-slate-500 text-sm  dark:text-slate-100">
          Transactions
        </p>
        <p className="flex-[0.6] text-sm font-medium text-gray-700  w-1/2 dark:text-gray-100 ">
          <Link
            to={`/merchants/transactions/business/${business.id}`}
            target="_blank"
            rel="noopener noreferrer"
            data-cy="merchant-business-transactions-link"
          >
            <Button
              variant="ghost"
              className="no-padding no-margin hover:underline"
              rightIcon={<ExternalLinkIcon className="w-4 h-4" />}
            >
              View Transactions
            </Button>
          </Link>
        </p>
      </div>
      <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900">
        <p className="flex-[0.4] text-slate-500 text-sm  dark:text-slate-100">
          KYB Details
        </p>
        <p className="flex-[0.6] text-sm font-medium text-gray-700  w-1/2 dark:text-gray-100 ">
          <Link
            to={`/merchants/kyb/${business.id}`}
            target="_blank"
            rel="noopener noreferrer"
            data-cy="merchant-business-kyb-link"
          >
            <Button
              variant="ghost"
              className="no-padding no-margin hover:underline"
              rightIcon={<ExternalLinkIcon className="w-4 h-4" />}
            >
              View KYB Details
            </Button>
          </Link>
        </p>
      </div>
      <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900">
        <p className="flex-[0.4] text-slate-500 text-sm  dark:text-slate-100">
          UBO Details
        </p>
        <p className="flex-[0.6] text-sm font-medium text-gray-700  w-1/2 dark:text-gray-100 ">
          <Link
            to={`/merchants/users/${person?.id}`}
            target="_blank"
            rel="noopener noreferrer"
            data-cy="merchant-business-user-profile-link"
          >
            <Button
              variant="ghost"
              className="no-padding no-margin hover:underline"
              rightIcon={<ExternalLinkIcon className="w-4 h-4" />}
            >
              View profile
            </Button>
          </Link>
        </p>
      </div>
      <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900">
        <p className="flex-[0.4] text-slate-500 text-sm  dark:text-slate-100">
          Webhook Logs
        </p>
        <p className="flex-[0.6] text-sm font-medium text-gray-700  w-1/2 dark:text-gray-100 ">
          <Link
            to={`/merchants/business/${business.id}/webhooks`}
            target="_blank"
            rel="noopener noreferrer"
            data-cy="merchant-business-webhooks-link"
          >
            <Button
              variant="ghost"
              className="no-padding no-margin hover:underline"
              rightIcon={<ExternalLinkIcon className="w-4 h-4" />}
            >
              View webhooks logs
            </Button>
          </Link>
        </p>
      </div>
      <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900">
        <p className="flex-[0.4] text-slate-500 text-sm  dark:text-slate-100">
          Approved for API
        </p>
        <div className="flex-[0.6] flex flex-row text-sm font-medium text-gray-700 gap-2 w-1/2 dark:text-gray-100 ">
          <div className="flex-1 flex items-center gap-2 truncate">
            <Badge
              label={isApprovedForAPI ? 'Yes' : 'No'}
              colorScheme={
                statusColors[isApprovedForAPI ? 'successful' : 'failed']
              }
              className="px-4 !rounded-3xl"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default MerchantListSummary;
